import { useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { motion, useReducedMotion } from "framer-motion";
import { Eye, IndianRupee, Loader2, Receipt, Search } from "lucide-react";
import { API, api, formatApiError } from "../../api";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { useCelebration } from "../../contexts/CelebrationContext";
import { formatCurrency, isPhoneQuery } from "../../lib/format";
import { fadeUp } from "../../lib/motion";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "../../components/ui/sonner";
import { FormField, isFieldValid } from "../../components/common/FormField";
import { SubmitButton } from "../../components/common/SubmitButton";
import { useSuccessPulse } from "../../hooks/useSuccessPulse";

// UX-FD-01 §1 SCR-P02-30/31/32: Take payment + Collect due share one form ("Collect due" just
// autofills the outstanding balance into the same amount field — one real POST /payments call,
// not two code paths). Pay-link/re-mandate (`POST /mandates`) needs `mandate.manage`, which is
// Manager-tier only on the existing backend and NOT on front_of_house_fd's default role — that
// panel only renders if `me.permissions` actually has it (server-enforced + hidden, not
// disabled). "Retry an existing mandate" is dropped entirely: there is no GET endpoint to look
// up a member's existing mandate id, so it isn't reachable from any UI without fabricating a
// lookup — flagged, not built.
export default function MoneyIn() {
  const { branchId, me } = useDeskData();
  const reducedMotion = useReducedMotion();
  const { celebrate } = useCelebration();
  const [query, setQuery] = useState("");
  const [submitted, setSubmitted] = useState("");
  const [member, setMember] = useState(null);
  const [mode, setMode] = useState("cash");
  const [amountRupees, setAmountRupees] = useState("");
  const [railRef, setRailRef] = useState("");
  const [providerRef, setProviderRef] = useState("");
  const canManageMandate = me?.permissions?.includes("mandate.manage");
  const [paySuccess, payPulse] = useSuccessPulse();
  const [mandateSuccess, mandatePulse] = useSuccessPulse();
  const [amountFieldKey, setAmountFieldKey] = useState(0);
  const [lastPaymentId, setLastPaymentId] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    clearTimeout(timerRef.current);
    if (query.trim().length < 2) { setSubmitted(""); return; }
    timerRef.current = setTimeout(() => { setMember(null); setLastPaymentId(null); setSubmitted(query.trim()); }, 250);
    return () => clearTimeout(timerRef.current);
  }, [query]);

  const searchQ = useQuery({
    queryKey: ["desk-money-search", submitted],
    queryFn: async () => {
      const params = isPhoneQuery(submitted) ? { phone: submitted.replace(/[\s-]/g, "") } : { name: submitted };
      return (await api.get("/members", { params: { ...params, limit: 10 } })).data.members;
    },
    enabled: !!submitted,
  });

  const balanceQ = useQuery({
    queryKey: ["desk-money-balance", member?.id],
    queryFn: async () => (await api.get(`/members/${member.id}/balance`)).data,
    enabled: !!member,
  });

  // The membership must be pinned on the payment (`membership_ref`) or the server never re-runs
  // `applyLedgerProjection` for it and dues_balance stays stale despite the payment landing in
  // the ledger correctly (bug caught by testing_agent_v4 iteration_24 against this exact
  // screen — fixed here, same ledger-derivation approach as MemberDetail.jsx since no
  // member-scoped membership-list endpoint exists).
  const ledgerQ = useQuery({
    queryKey: ["desk-money-ledger", member?.id],
    queryFn: async () => (await api.get("/ledger", { params: { member_ref: member.id } })).data.entries,
    enabled: !!member,
  });
  const membershipRef = (() => {
    const withRef = (ledgerQ.data || []).filter((e) => e.membership_ref);
    return withRef.length ? withRef[withRef.length - 1].membership_ref : null;
  })();

  const paymentMutation = useMutation({
    mutationFn: () => api.post(
      "/payments",
      { mode, amount: Math.round(Number(amountRupees) * 100), branch_id: branchId, member_ref: member.id, membership_ref: membershipRef, rail_ref: mode === "digital" ? railRef : null },
      { headers: { "Idempotency-Key": crypto.randomUUID() } },
    ),
    onSuccess: (res) => payPulse(() => {
      celebrate("payment_collected", `${formatCurrency(Math.round(Number(amountRupees) * 100))} collected from ${member.profile.name}.`);
      setLastPaymentId(res.data.id);
      setInvoiceId(null);
      setAmountRupees(""); setRailRef(""); setAmountFieldKey((k) => k + 1);
      balanceQ.refetch();
    }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const [invoiceId, setInvoiceId] = useState(null);
  const invoiceMutation = useMutation({
    mutationFn: () => api.post("/invoices/for-payment", { payment_ref: lastPaymentId }),
    onSuccess: (res) => setInvoiceId(res.data.id),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  const mandateMutation = useMutation({
    mutationFn: () => api.post("/mandates", { member_ref: member.id, provider_ref: providerRef, origin: "re_mandate" }),
    onSuccess: () => mandatePulse(() => { toast.success("Mandate created."); setProviderRef(""); }),
    onError: (e) => toast.error(formatApiError(e).message),
  });

  // Logical, real-time cap on "Take payment": when the member has dues on file, the amount
  // field can't exceed what's actually outstanding — fat-fingering extra zeros (or any other
  // over-collection) is blocked at entry, not just flagged after the fact. A member with zero
  // dues has no fixed ceiling here (this screen also supports pre-collecting an advance, which
  // the ledger already models via the `advance` entry type).
  const duesBalance = balanceQ.data?.dues_balance ?? 0;
  const maxCollectable = duesBalance > 0 ? duesBalance / 100 : null;

  return (
    <div data-testid={DESK.moneyPage} className="max-w-lg space-y-6">
      <motion.div {...fadeUp(reducedMotion)}>
        <h1 className="text-2xl font-bold" style={{ color: "#141416" }}>Money in</h1>
        <p className="text-sm mt-0.5" style={{ color: "#68686E" }}>Find a member, then take a payment or collect their due.</p>
      </motion.div>

      <motion.div {...fadeUp(reducedMotion, 0.04)} className="relative max-w-sm">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "#68686E" }} />
        <Input data-testid={DESK.moneySearchInput} placeholder="Name or phone…" value={query} onChange={(e) => setQuery(e.target.value)} className="pl-9" />
      </motion.div>

      {searchQ.isLoading && <p className="text-sm flex items-center gap-2" style={{ color: "#68686E" }}><Loader2 size={14} className="animate-spin" /> Searching…</p>}

      {!member && searchQ.data?.map((m) => (
        <button key={m.id} onClick={() => { setMember(m); setLastPaymentId(null); setInvoiceId(null); }} className="w-full flex items-center justify-between rounded-[12px] px-4 py-3 text-left scarlet-focusable" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
          <div>
            <p className="font-semibold text-sm" style={{ color: "#141416" }}>{m.profile.name}</p>
            <p className="text-xs" style={{ color: "#68686E" }}>{m.profile.phone}</p>
          </div>
        </button>
      ))}

      {member && (
        <motion.div {...fadeUp(reducedMotion)} className="space-y-4">
          <div data-testid={DESK.moneyBalanceCard} className={`rounded-[14px] p-4 flex items-center justify-between ${paySuccess ? "celebrate-pop" : ""}`} style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
            <div>
              <p className="font-semibold text-sm" style={{ color: "#141416" }}>{member.profile.name}</p>
              <p className="text-xs" style={{ color: "#68686E" }}>Dues outstanding</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold" style={{ color: balanceQ.data?.dues_balance > 0 ? "var(--scarlet-neg)" : "#141416" }}>{formatCurrency(balanceQ.data?.dues_balance ?? 0)}</p>
              {balanceQ.data?.dues_balance > 0 && (
                <button data-testid={DESK.collectDueButton} onClick={() => setAmountRupees(String(balanceQ.data.dues_balance / 100))} className="text-xs font-semibold hover:opacity-70" style={{ color: "#D6202B" }}>
                  Collect this due
                </button>
              )}
            </div>
          </div>

          <div className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
            <p className="text-sm font-semibold" style={{ color: "#141416" }}>Take payment</p>
            <Select onValueChange={setMode} value={mode}>
              <SelectTrigger data-testid={DESK.paymentModeSelect}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="digital">Digital (UPI/Card)</SelectItem>
              </SelectContent>
            </Select>
            <FormField
              key={amountFieldKey} required type="number" testId={DESK.paymentAmountInput} placeholder="Amount (₹) *" value={amountRupees}
              onChange={(v) => setAmountRupees(v === "" || maxCollectable == null ? v : String(Math.min(Number(v) || 0, maxCollectable)))}
              helperText={maxCollectable != null ? `Up to ${formatCurrency(duesBalance)} outstanding — excess amounts aren't accepted.` : undefined}
            />
            {mode === "digital" && <FormField required testId="desk-money-rail-ref-input" placeholder="Transaction reference *" value={railRef} onChange={setRailRef} />}
            <SubmitButton
              testId={DESK.paymentSubmitButton}
              disabled={!isFieldValid("number", true, amountRupees) || (mode === "digital" && !railRef.trim())}
              pending={paymentMutation.isPending} success={paySuccess}
              onClick={() => paymentMutation.mutate()}
              fullWidth className="gap-2" label={<><IndianRupee size={14} /> Collect payment</>} successLabel="Collected"
            />
            {lastPaymentId && (
              invoiceId ? (
                <div className="flex gap-2">
                  <Button type="button" variant="outline" data-testid={`${DESK.moneyGenerateInvoiceButton}-show`} onClick={() => window.open(`${API}/invoices/${invoiceId}/pdf?inline=true`, "_blank")} className="flex-1 gap-2">
                    <Eye size={14} /> Show invoice
                  </Button>
                  <Button type="button" variant="outline" data-testid={DESK.moneyGenerateInvoiceButton} onClick={() => window.open(`${API}/invoices/${invoiceId}/pdf`, "_blank")} className="flex-1 gap-2">
                    <Receipt size={14} /> Download
                  </Button>
                </div>
              ) : (
                <Button
                  type="button" variant="outline" data-testid={DESK.moneyGenerateInvoiceButton}
                  disabled={invoiceMutation.isPending} onClick={() => invoiceMutation.mutate()} className="w-full gap-2"
                >
                  {invoiceMutation.isPending ? <Loader2 size={14} className="animate-spin" /> : <Receipt size={14} />} Generate invoice
                </Button>
              )
            )}
          </div>

          {canManageMandate && (
            <div className="rounded-[14px] p-5 space-y-3" style={{ background: "#fff", border: "1px solid #EBEAE5" }}>
              <p className="text-sm font-semibold" style={{ color: "#141416" }}>Set up auto-debit mandate</p>
              <FormField required testId={DESK.mandateProviderInput} placeholder="Provider reference (tokenized) *" value={providerRef} onChange={setProviderRef} />
              <SubmitButton
                testId={DESK.mandateCreateButton} disabled={!providerRef.trim()}
                pending={mandateMutation.isPending} success={mandateSuccess}
                onClick={() => mandateMutation.mutate()}
                fullWidth variant="outline" label="Create mandate" successLabel="Created"
              />
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
